import { useRef } from 'react';

const VideoAnalysisLogger = () => {
    const analysisLog = useRef({
        metrics: {
            facialEngagement: 0,
            goodPosturePercentage: 0,
            handsVisiblePercentage: 0,
            totalDuration: 0,
        },
        sessionTotals: {
            facialEngagementSum: 0,
            goodPostureFrames: 0,
            handsVisibleFrames: 0,
            totalFrames: 0
        }
    });

    const processInterval = useRef(null);
    const startTime = useRef(null);
    const frameDataBuffer = useRef({
        faceExpressions: [],
        poseData: [],
        handGestures: []
    });

    const logAnalysis = (data) => {
        const TEN_SECONDS = 10000;
        const currentTime = Date.now();
        const timestamp = startTime.current ? currentTime - startTime.current : 0;

        // Update session totals
        if (data.faceExpressions?.[0]) {
            analysisLog.current.sessionTotals.totalFrames++;
            // Add facial engagement to sum
            if (data.faceExpressions[0].engagement) {
                analysisLog.current.sessionTotals.facialEngagementSum += data.faceExpressions[0].engagement;
            }
        }

        // Process posture data
        if (data.poseData?.[0]) {
            if (data.poseData[0].posture === true) {
                analysisLog.current.sessionTotals.goodPostureFrames++;
            }
        }

        // Process hand visibility data
        if (data.handGestures?.[0]) {
            if (data.handGestures[0].visible === true) {
                analysisLog.current.sessionTotals.handsVisibleFrames++;
            }
        }

        // Buffer the frame data for real-time analysis
        if (data.faceExpressions?.[0]) {
            frameDataBuffer.current.faceExpressions.push({
                timestamp,
                data: data.faceExpressions[0]
            });
        }

        if (data.poseData?.[0]) {
            frameDataBuffer.current.poseData.push({
                timestamp,
                data: data.poseData[0]
            });
        }

        if (data.handGestures?.[0]) {
            frameDataBuffer.current.handGestures.push({
                timestamp,
                data: data.handGestures[0]
            });
        }

        // Trim buffers to keep only last 10 seconds
        const cutoffTime = timestamp - TEN_SECONDS;
        frameDataBuffer.current.faceExpressions = frameDataBuffer.current.faceExpressions
            .filter(entry => entry.timestamp > cutoffTime);
        frameDataBuffer.current.poseData = frameDataBuffer.current.poseData
            .filter(entry => entry.timestamp > cutoffTime);
        frameDataBuffer.current.handGestures = frameDataBuffer.current.handGestures
            .filter(entry => entry.timestamp > cutoffTime);
    };

    const calculateMetrics = () => {
        const currentTime = Date.now();
        const totalDuration = startTime.current ? (currentTime - startTime.current) / 1000 : 0;

        if (totalDuration > 0) {
            const { sessionTotals } = analysisLog.current;

            if (sessionTotals.totalFrames > 0) {
                analysisLog.current.metrics = {
                    facialEngagement: sessionTotals.facialEngagementSum / sessionTotals.totalFrames,
                    goodPosturePercentage: (sessionTotals.goodPostureFrames / sessionTotals.totalFrames) * 100,
                    handsVisiblePercentage: (sessionTotals.handsVisibleFrames / sessionTotals.totalFrames) * 100,
                    totalDuration
                };
            }
        }
    };

    const startLogging = () => {
        startTime.current = Date.now();
        processInterval.current = setInterval(calculateMetrics, 1000);
    };

    const stopLogging = () => {
        if (processInterval.current) {
            clearInterval(processInterval.current);
            calculateMetrics(); // Final calculation
        }

        // Return only the final metrics, not the raw data
        return {
            metrics: analysisLog.current.metrics
        };
    };

    const resetLogger = () => {
        frameDataBuffer.current = {
            faceExpressions: [],
            poseData: [],
            handGestures: []
        };

        analysisLog.current = {
            metrics: {
                facialEngagement: 0,
                goodPosturePercentage: 0,
                handsVisiblePercentage: 0,
                totalDuration: 0,
            },
            sessionTotals: {
                facialEngagementSum: 0,
                goodPostureFrames: 0,
                handsVisibleFrames: 0,
                totalFrames: 0
            }
        };
        startTime.current = null;
    };

    return {
        logAnalysis,
        startLogging,
        stopLogging,
        resetLogger,
        getAnalysisData: () => ({
            metrics: analysisLog.current.metrics
        })
    };
};

export default VideoAnalysisLogger;